import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ToastVariants } from '@dotcom/crown-ui';
import { useAnalyticsContext } from '../../../context/analytics-context';
import { hideLoadingScreen, setIsToasterOpen, useAppContext } from '../../../context/app-context';
import { useDataContext } from '../../../context/data-context/use-data-context';
import { useAddressBookAddresses } from '../../../features/address-book/hooks/use-address-book-addresses';
import {
  Address,
  AddressForm,
  AddressRecipientUpdate,
  AddressResponseData,
  AddressToSave,
  AddressTypeCode,
  AddressTypes,
  ErrorType,
  QuickAddressSubmitData,
} from '../../../global-types/addresses';
import { EnvelopeType } from '../../../global-types/envelope';
import { ErrorResponse } from '../../../global-types/services';
import { useSystemErrorHandling } from '../../../hooks';
import { useAddressNavigation } from '../../../hooks/use-address-navigation';
import { useIsOneToMany } from '../../../hooks/use-is-one-to-many';
import { saveAddress } from '../../../services';
import { addressOptions, updateAddress } from '../../../services/address';
import { getIsDigitalGreeting, helperSettingsConfig, isLoggedIn } from '../../../utils';
import { getAddressError } from '../../../utils/address/get-address-error';
import { mapAddressesByType } from '../../../utils/address/map-address-by-type';
import { buildOneToOneAddresses } from '../../../utils/build-one-to-one-addresses';
import { useLoadExistingProject } from '../../editor/hooks/use-load-existing-project';
import { HandleAddressResubmit } from '../address-types';
import { useEnvelopeAddresses } from './use-envelope-addresses';

export type UseHandleSubmit = {
  openAddressConfirmationDialog: () => void;
  closeAddressConfirmationDialog: () => void;
  addAddressToValidate: (
    address: Address & {
      address_id?: string;
    },
    errorType: ErrorType,
    step: number,
    contactId: any,
  ) => void;
  envelope?: EnvelopeType | null;
};

export const useHandleSubmit = ({
  openAddressConfirmationDialog,
  closeAddressConfirmationDialog,
  addAddressToValidate,
  envelope,
}: UseHandleSubmit) => {
  // state
  const [isAddressLoading, setIsAddressLoading] = useState(false);

  // contexts
  const {
    initializedDataState: { data, addressData, isRecipientAddress },
    setAddressData,
    setIsRecipientAddress,
  } = useDataContext();
  const { appDispatch } = useAppContext();
  const { trackClickNextToAddressSender, trackManuallyAddRecipientQuickAddress } = useAnalyticsContext();

  // hooks
  const { t } = useTranslation();
  const { loadExistingProject } = useLoadExistingProject();
  const { onSystemError } = useSystemErrorHandling();
  const { navigateWithParams } = useAddressNavigation();
  const isOneToMany = useIsOneToMany();
  const queryClient = useQueryClient();
  const { saveAddressToAddressBook } = useAddressBookAddresses();
  const addressTypesToFill = useEnvelopeAddresses();

  // memoized constants
  const lastProjectLoaded = useMemo(() => sessionStorage.getItem('lastProjectLoaded'), []);
  const isDigitalGreeting = useMemo(() => getIsDigitalGreeting(), []);
  const projectId = useMemo(() => data?.project_id, [data?.project_id]);

  // ref to check if the project is loaded
  const isDataLoaded = useRef<boolean>(false);

  // maps the address form to the AddressToSave type to be saved to the project
  const mapAddressToSave = (address: AddressForm | Address | AddressResponseData): AddressToSave => {
    return {
      address_line_1: address.address_line_1,
      address_line_2: address.address_line_2,
      address_type_code: address.address_type_code,
      city: address.city,
      company_name: address.company_name,
      country_code: address.country_code,
      first_name: address.first_name,
      last_name: address.last_name,
      skip_usps_validation: (address as Address).skip_usps_validation || false,
      state_code: address.state_code,
      zip: address.zip,
    };
  };

  // shows error toast from the app context state
  const showErrorToast: (error: string, title?: string) => void = useCallback(
    (error, title) => {
      setIsToasterOpen(appDispatch, {
        variant: ToastVariants.Error,
        title: title || t('addressView.failedToast'),
        children: error,
      });
    },
    [appDispatch, t],
  );

  // handles errors when saving or updating an address
  // adding the error to the validation state and hiding the loading screen
  const handleError: (
    error: ErrorResponse,
    address: Address | AddressResponseData,
    mappedAddress: AddressToSave | AddressRecipientUpdate,
  ) => void = useCallback(
    (error, address, mappedAddress) => {
      const addressError = getAddressError((error as ErrorResponse).data.errors);
      addAddressToValidate(
        {
          ...mappedAddress,
          address_type_code: (mappedAddress as AddressToSave).address_type_code || address.address_type_code,
          address_id: (address as AddressResponseData).address_id || '',
          skip_usps_validation: (address as Address).skip_usps_validation || false,
        },
        addressError?.description as ErrorType,
        isRecipientAddress ? 1 : 0,
        (address as AddressResponseData).address_id,
      );
      hideLoadingScreen(appDispatch);
    },
    [addAddressToValidate, appDispatch, isRecipientAddress],
  );

  // saves the address validating the input and mapping the payload
  // to send the correct data to the API service
  const handleSaveAddress: (addressToSave: AddressForm | Address | AddressResponseData) => Promise<void> = useCallback(
    async (addressToSave) => {
      if (!projectId) {
        // todo: this is just a temporary fix to load the project when the user tries to save an address
        //  this should be removed once we fix it in the AddressView
        await loadExistingProject(lastProjectLoaded || '', isDataLoaded);
        throw new Error('project_id_not_defined');
      }

      setIsAddressLoading(true);

      const mappedAddressToSave = mapAddressToSave(addressToSave);

      return saveAddress(projectId, mappedAddressToSave)
        .then((response) => {
          if (!response.data) return;

          const mappedAddress = mapAddressesByType([response.data]);
          setAddressData(mappedAddress);
        })
        .catch((error: ErrorResponse) => {
          if (error?.status === 422) {
            handleError(error, addressToSave, mappedAddressToSave);
            throw new Error('address_save_failed');
          }

          hideLoadingScreen(appDispatch);
          onSystemError(error);
        })
        .finally(() => {
          setIsAddressLoading(false);
        });
    },
    [appDispatch, handleError, lastProjectLoaded, loadExistingProject, onSystemError, projectId, setAddressData],
  );

  // updates the address validating the input and mapping the payload
  // to send the correct data to the API service
  const handleUpdateAddress: (addressToUpdate: Address | AddressResponseData, addressId: string) => Promise<void> =
    useCallback(
      async (addressToUpdate, addressId) => {
        if (!projectId) {
          // todo: this is just a temporary fix to load the project when the user tries to save an address
          //  this should be removed once we fix it in the AddressView
          await loadExistingProject(lastProjectLoaded || '', isDataLoaded);
          throw new Error('project_id_not_defined');
        }

        const mappedAddressToUpdate: AddressRecipientUpdate = {
          first_name: addressToUpdate.first_name,
          last_name: addressToUpdate.last_name,
          company_name: addressToUpdate.company_name,
          address_line_1: addressToUpdate.address_line_1,
          address_line_2: addressToUpdate.address_line_2,
          city: addressToUpdate.city,
          state_code: addressToUpdate.state_code || '',
          zip: addressToUpdate.zip,
          country_code: addressToUpdate.country_code,
          skip_usps_validation: (addressToUpdate as Address).skip_usps_validation,
        };

        setIsAddressLoading(true);

        return updateAddress(projectId, addressId, mappedAddressToUpdate)
          .then((response) => {
            if (!response.data) return;

            const mappedAddress = mapAddressesByType([response.data]);
            setAddressData(mappedAddress);
          })
          .catch((error: ErrorResponse) => {
            if (error?.status === 422) {
              handleError(error, addressToUpdate, mappedAddressToUpdate);
              throw new Error('address_update_failed');
            }

            hideLoadingScreen(appDispatch);
            onSystemError(error);
          })
          .finally(() => {
            setIsAddressLoading(false);
          });
      },
      [appDispatch, handleError, lastProjectLoaded, loadExistingProject, onSystemError, projectId, setAddressData],
    );

  // saves the address to the address book
  // validating the input and mapping the payload
  // to send the correct data to the API service
  // using the TSQ client
  const handleSaveToAddressBook: (address: AddressForm) => Promise<void> = useCallback(
    async (address) => {
      const contactPaging = await queryClient.ensureInfiniteQueryData(addressOptions.getQuickAddressesInfiniteQuery());
      const totalContactsStored = contactPaging.pages[0].paging.total_items ?? 0;

      const allowedContactsToSave = helperSettingsConfig.limitSaveQuickAddresses - totalContactsStored;
      // Contact limit has been exceeded
      if (allowedContactsToSave <= 0) {
        // This loop is being hit when the user has 0 contacts exactly and tries to add a quick address during the 1:1 flow
        showErrorToast(t('addressView.contactLimitError') || '', t('addressView.contactsErrorTitle') || '');
        return;
      }

      const mappedQuickAddress: QuickAddressSubmitData = {
        first_name: address.first_name,
        last_name: address.last_name,
        company_name: address.company_name,
        address_line_1: address.address_line_1,
        address_line_2: address.address_line_2,
        city: address.city,
        state_code: address.state_code,
        zip: address.zip,
        country_code: address.country_code,
        contact_type_code: 'A',
        skip_address_verification: true,
      };

      await saveAddressToAddressBook.mutateAsync(mappedQuickAddress);
      if (address.isQuickAddress && address.address_type_code === AddressTypeCode.RECIPIENT) {
        trackManuallyAddRecipientQuickAddress();
      }
    },
    [queryClient, saveAddressToAddressBook, showErrorToast, t],
  );

  // onSubmit validates the action to be taken
  // and calls the correct function to save or update the address
  const onSubmit: (address: AddressForm | undefined) => Promise<void> = useCallback(
    async (address) => {
      if (!address) return;

      const addressId = address.address_id;
      if (address && addressId) {
        // updating the sender and recipient addresses
        return handleUpdateAddress(address, addressId).catch(() => {
          setIsRecipientAddress(false);
          throw new Error('Error updating address: ' + address.address_type_code);
        });
      } else {
        return handleSaveAddress(address).catch(() => {
          setIsRecipientAddress(false);
          throw new Error('Error saving address: ' + address.address_type_code);
        });
      }
    },
    [handleSaveAddress, handleUpdateAddress, setIsRecipientAddress],
  );

  // handles the submit of the address form
  // takes the AddressForm values and maps the addresses to sender/recipient
  // based on the address_type_code
  // also validates when the user is saving a 1:M (return-only) or 1:1 card
  const handleAddressSubmit: (address: AddressForm) => Promise<void> = useCallback(
    async (address) => {
      if (isDigitalGreeting) {
        return;
      }

      // mapping the address to sender/recipient
      const mappedAddress = buildOneToOneAddresses(address);

      // todo: create a map to get the current address type const addrType = addressTypesMap[address.address_type_code];
      //  where const addressTypesMap = { [AddressTypeCode.RECIPIENT]: AddressTypes.RECIPIENT, [AddressTypeCode.SENDER]: AddressTypes.SENDER }
      const currentAddressType =
        address.address_type_code === AddressTypeCode.RECIPIENT ? AddressTypes.RECIPIENT : AddressTypes.SENDER;

      // ---> return-only flow
      const totalSteps = addressTypesToFill.length;
      if (totalSteps === 1) {
        // this should save the address (return/sender only)
        onSubmit(address)
          .then(() => {
            navigateWithParams('/card/customization/preview', envelope);
          })
          .catch(() => {
            openAddressConfirmationDialog();
          });
        return;
      }
      // <--- return-only flow

      // ---> 1:1/sns flow for 1 return and 1 recipient
      if (address.address_type_code === AddressTypeCode.SENDER) {
        setIsRecipientAddress(true);
        setAddressData({
          [currentAddressType]: { ...addressData[`${currentAddressType}`], ...mappedAddress?.sender },
        });
        trackClickNextToAddressSender();

        return;
      }

      setAddressData({
        [currentAddressType]: { ...addressData[`${currentAddressType}`], ...mappedAddress?.recipient },
      });

      if (!addressData.sender) {
        // safety check to make sure the sender address is defined
        throw new Error('sender_address_not_defined');
      }

      // get sender address from the address data
      const senderAddress: AddressForm = {
        ...addressData.sender,
        address_line_1: addressData.sender.address_line_1,
        address_line_2: addressData.sender.address_line_2,
        address_type_code: addressData.sender.address_type_code,
        city: addressData.sender.city,
        company_name: addressData.sender.company_name,
        country_code: addressData.sender.country_code,
        first_name: addressData.sender.first_name,
        isQuickAddress: addressData.sender.isQuickAddress || false,
        last_name: addressData.sender.last_name,
        zip: addressData.sender.zip,
        address_id: addressData.sender.address_id || '',
        skip_usps_validation: addressData.sender.skip_usps_validation || false,
      };

      // saving the sender address and recipient address
      await Promise.all([onSubmit(senderAddress), onSubmit(address)])
        .then(() => {
          // save to address book if logged in and the address is quick
          // only triggering the save to address book when the submit is successful
          if (isLoggedIn()) {
            if (senderAddress.isQuickAddress) void handleSaveToAddressBook(senderAddress);
            if (address.isQuickAddress) void handleSaveToAddressBook(address);
          }

          // navigating to the preview page only when submit is successful
          navigateWithParams('/card/customization/preview', envelope);
        })
        .catch(() => {
          // opens the address confirmation dialog / address validation
          openAddressConfirmationDialog();
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isDigitalGreeting,
      addressTypesToFill.length,
      addressData,
      setAddressData,
      onSubmit,
      navigateWithParams,
      setIsRecipientAddress,
      trackClickNextToAddressSender,
      handleSaveToAddressBook,
    ],
  );

  // handles the resubmit action when the user wants to skip the address validation
  // and resubmit the address setting custom values to allow the address to be saved
  // for now we set the skip_usps_validation to true only, however, we can add more
  // custom values to the resubmit action
  const handleResubmit = useCallback<HandleAddressResubmit>(
    async ({ senderAddress, recipientAddress }) => {
      setIsAddressLoading(true);

      await Promise.all([
        onSubmit(senderAddress)
          .catch((error) => {
            onSystemError(error);
          })
          .finally(() => {
            setIsAddressLoading(false);
          }),
        onSubmit(recipientAddress)
          .catch((error) => {
            onSystemError(error);
          })
          .finally(() => {
            setIsAddressLoading(false);
          }),
      ]);

      navigateWithParams('/card/customization/preview', envelope);
      closeAddressConfirmationDialog();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOneToMany, navigateWithParams, onSubmit, onSystemError],
  );

  return {
    isAddressLoading,
    setIsAddressLoading,
    handleAddressSubmit,
    handleSaveToAddressBook,
    handleResubmit,
  };
};
